import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Timeline from "@galaxy-io/dls/timeline/Timeline";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import Widget from "@galaxy-io/dls/widget/Widget";
import type { WorkflowCatalogEntry } from "@gtm/contracts/workflows/catalog";
import { styled } from "@linaria/react";
import { PlayIcon } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { DrawerList, DrawerTile } from "@/components/workspace/drawer-content";
import { SelectField } from "@/components/workspace/select-field";
import { SourceIcon } from "@/components/workspace/source-icon";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";
import { toastDefaults } from "@/lib/toast";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[16]};
  padding: ${t.space[16]};
  min-width: 0;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[8]};
  min-width: 0;
`;
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[12]};
`;
// The run drawer lists finished steps in a divided card. Before the run there
// is no outcome to report, so the same card keeps the order and drops the
// status column.
// Collection workflows take one source. The API supplies
// tenant/user identity and the manual trigger; those are never editable inputs.
export function GitHubWorkflowRunDialog({
  open,
  workflow,
  repositories,
  sourceId,
  onSourceChange,
  onClose,
  restoreFocus,
}: {
  open: boolean;
  workflow: {
    key: Exclude<WorkflowCatalogEntry["key"], "github.enrich_person">;
    name: string;
    description: string;
    tasks: ReadonlyArray<{ key: string; name: string }>;
  };
  repositories: Array<{ id: string; name: string; provider: string }>;
  sourceId: string;
  onSourceChange: (sourceId: string) => void;
  onClose: () => void;
  restoreFocus?: () => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const { toast } = useToast();
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const submitting = useRef(false);
  const requestedAt = useRef(new Map<string, string>());
  const repository = repositories.find((source) => source.id === sourceId);
  const provider = workflow.key.split(".")[0];

  async function launch() {
    setFormError(undefined);
    if (!repository || submitting.current) return;
    submitting.current = true;
    setPending(true);
    // A trigger is a workflow ID with a time attached. Keep the first press's
    // time after an uncertain HTTP result so retrying this form targets the
    // same workflow ID instead of starting the same collection twice.
    let at = requestedAt.current.get(sourceId);
    if (!at) {
      at = new Date().toISOString();
      requestedAt.current.set(sourceId, at);
    }
    try {
      await unwrap(
        apiClient().POST("/workflows/runs", {
          body: { workflowKey: workflow.key, sourceId, requestedAt: at },
        }),
      );
      toast({
        variant: ToastVariant.SUCCESS,
        header: `${workflow.name} started`,
        description: sourceLabel(repository.provider, repository.name),
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      onClose();
      router.refresh();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Try again.");
    } finally {
      submitting.current = false;
      setPending(false);
    }
  }

  useDialogReset(open, () => {
    setFormError(undefined);
    setPending(false);
    submitting.current = false;
    requestedAt.current.clear();
  });
  return (
    <SurfaceDialog
      restoreFocus={restoreFocus}
      title={workflow.name}
      headerDescription={workflow.description}
      headerLeading={
        <DrawerTile>
          <SourceIcon source={provider} size={22} />
        </DrawerTile>
      }
      open={open}
      flush
      onClose={onClose}
      busy={pending}
      footer={
        <Button
          label="Run workflow"
          icon={PlayIcon}
          isLoading={pending}
          isDisabled={!repository || pending}
          onClick={launch}
        />
      }
    >
      {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
      <Body>
        <Section>
          <SectionHeader>
            <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
              Steps
            </Text>
            <Text size={TextSize.CAPTION} variant={TextVariant.SECONDARY}>
              {workflow.tasks.length === 1 ? "1 step" : `${workflow.tasks.length} steps`}
            </Text>
          </SectionHeader>
          <Widget>
            <Timeline
              aria-label="Workflow tasks"
              items={workflow.tasks.map((task) => ({ id: task.key, label: task.name }))}
            />
          </Widget>
        </Section>
        <DrawerList>
          <DescriptionList
            items={[
              {
                label: "Trigger",
                value: (
                  <>
                    <Status value="manual" />
                  </>
                ),
              },
              {
                label: "Workflow",
                value: (
                  <>
                    <Text
                      size={TextSize.BODY_SM}
                      variant={TextVariant.SECONDARY}
                      family={FontFamily.MONO}
                    >
                      {workflow.key}
                    </Text>
                  </>
                ),
              },
            ]}
          />
        </DrawerList>
        <Section>
          <SelectField
            label={provider === "reddit" ? "Subreddit" : "Repository"}
            value={sourceId}
            onChange={onSourceChange}
            options={repositories.map((source) => ({
              label: sourceLabel(source.provider, source.name),
              value: source.id,
              icon: <SourceIcon source={source.provider} size={16} />,
            }))}
            disabled={pending || !repositories.length}
            search
          />
          {!repositories.length && (
            <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
              Track a matching source in Integrations first.
            </Text>
          )}
        </Section>
      </Body>
    </SurfaceDialog>
  );
}
